// App
import Vue from 'vue'
import App from './App'
import router from './router'
import createApolloProvider from './apollo'
import { mapGetters, mapState } from 'vuex'
import defaultConf from '../config/runtimeConf/default'
// Services
import Api from './services/api'
import store from './services/store'
import FileUploadService from './services/FileUploadService'
import { bus, EVENTS } from './services/bus/index.js'
// Modules
import jwtDecode from 'jwt-decode'
import { merge, concat } from 'lodash'
import VueAnalytics from 'vue-analytics'
import algoliasearch from 'algoliasearch'
import axios from 'axios'
import { Realtime } from 'ably'
import * as VueGoogleMaps from 'vue2-google-maps'
import { realtimeEvents, adminRealtimeEvents } from '@/utils/Realtime'
// Utils
import '@/utils/DefaultFilters'
import '@/utils/GlobalComponents'
import * as Sentry from '@sentry/browser'

require('animate.css')
require('./styles/animation.css')
require('moment/locale/fr.js')

window.Vue = Vue
// Set config
let _configs = Object.assign({}, defaultConf)
const envConf = {
  APIROOT: process.env.APIROOT,
  APIURL: process.env.APIURL,
  APPHOST: process.env.APPHOST,
  COMPANY_COLOR: process.env.COMPANY_COLOR,
  FAVICON_32: process.env.FAVICON_32,
  FAVICON_16: process.env.FAVICON_16,
  FAVICON_APPLE: process.env.FAVICON_APPLE,
  LOGO_LOGIN: process.env.LOGO_LOGIN,
  LOGO_MAIN: process.env.LOGO_MAIN,
  LOGO_MAIN_LANDSCAPE: process.env.LOGO_MAIN_LANDSCAPE,
  ILLUSTRATION_LOGIN: process.env.ILLUSTRATION_LOGIN,
  MOBILE_HOST: process.env.MOBILE_HOST,
  NODE_ENV: process.env.NODE_ENV,
  GRAPHQL_API: process.env.GRAPHQL_API,
  STATS_ENABLED: process.env.STATS_ENABLED,
  GED_ENABLED: process.env.GED_ENABLED || 0,
  VADIMM_PUBLIC: process.env.VADIMM_PUBLIC,
  FAKE_STATS: process.env.FAKE_STATS || 0
}
merge(_configs, envConf)
Object.defineProperty(Vue.prototype, '$configs', {
  value: _configs
})

Vue.config.productionTip = false

Vue.use(Api, {
  host: _configs.APIURL
})

/* init directive helper to avoid failed to resolve directive issue
  full directive init here : @/utils/HelperDirective */
Vue.directive('helper')

/* eslint-disable no-new */
new Vue({
  el: '#app',
  store,
  provide: createApolloProvider(_configs.GRAPHQL_API).provide(),
  router,
  render: h => h(App),
  data () {
    return {
      lastAppUpdate: null,
      loadDatasTimeout: null,
      ablyClient: null
    }
  },
  computed: {
    ...mapState(['userId', 'currentUser', 'poles', 'promoteurs', 'companies', 'origins']),
    ...mapGetters(['isAdmin', 'isExternal', 'isManager', 'isSeller', 'currentUserPk', 'isEditor', 'isLog', 'isOperator'])
  },
  methods: {
    initGoogleClient () {
      if (window && window.gapi && window.gapi.client) {
        window.gapi.client.init({
          apiKey: this.$configs.GOOGLE_API_KEY,
          clientId: this.$configs.GOOGLE_CLIENT_ID,
          discoveryDocs: [
            'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest',
            'https://www.googleapis.com/discovery/v1/apis/people/v1/rest'
          ],
          scope: 'https://www.googleapis.com/auth/calendar'
        }).then(res => {
          this.$store.commit('loadGoogleApi')
          window.gapi.auth2.getAuthInstance().isSignedIn.listen(this.onGoogleLogin)
          this.onGoogleLogin(window.gapi.auth2.getAuthInstance().isSignedIn.get())
        }).catch(err => console.log(err))
      }
    },
    onGoogleLogin (isLog) {
      if (isLog) {
        this.$store.commit('googleLogin')
        window.gapi.client.people.people.get({
          resourceName: 'people/me',
          personFields: 'emailAddresses'
        }).then(r => {
          this.$store.commit('setGoogleProfile', r.result)
          this.loadCalendars()
        }, (err) => console.log(err))
      } else {
        this.$store.commit('googleLogout')
      }
    },
    loadCalendars () {
      window.gapi.client.calendar.calendarList.list({})
        .then((r) => {
          this.$store.commit('setGoogleCalendar', r.result.items)
        })
    },
    initApp () {
      this.getStates()
      this.getPoles()
      this.getOrigins()
      this.getCompanies()
      this.getPromoteurs()
      this.getAllStates()
      this.loadCurrentUser()
      this.loadUsersLength()
      this.loadWishlist()
      this.loadEvents()
      if (!this.isEditor && !this.isOperator) {
        this.loadRunningOptions()
        this.loadRunningDenunciations()
      }
      if (this.isLog) {
        this.loadProfils()
      }
    },
    initLiveApi (token) {
      let client = new Realtime(token)
      this.ablyClient = client
      if (this.isAdmin) {
        const channelId = 'admin-channel'
        let adminChannel = client.channels.get(channelId)
        adminChannel.subscribe(event => {
          if (event.name && event.data && event.name in adminRealtimeEvents) {
            adminRealtimeEvents[event.name](event.data)
          }
        })
      }
      const channelId = `user-channel-${this.userId}`
      let channel = client.channels.get(channelId)
      channel.subscribe(event => {
        console.log('event initLiveApi : ' + event)
        if (event.name && event.data && event.name in realtimeEvents) {
          realtimeEvents[event.name](event.data)
        }
      })
    },
    getStates () {
      let query = {
        clean: true
      }
      this.$api.stateService.get(query).then(r => {
        this.$store.commit('setStates', {
          states: r.data
        })
      })
    },
    async getOrigins (page = 1) {
      let request = await this.$api.originService.get({ page })
      if (page > 1) {
        let totalOrigins = concat(request.data.items, this.origins)
        this.$store.commit('setOrigins', { origins: totalOrigins })
      } else {
        this.$store.commit('setOrigins', { origins: request.data })
      }
      if (request.data.page < request.data.pages) {
        page++
        this.getOrigins(page)
      }
    },
    async getCompanies (page = 1) {
      let request = await this.$api.companyService.get({ page })
      if (page > 1) {
        let totalCompanies = concat(request.data.items, this.companies)
        this.$store.commit('setCompanies', { companies: totalCompanies })
      } else {
        this.$store.commit('setCompanies', { companies: request.data.items })
      }
      if (request.data.page < request.data.pages) {
        page++
        this.getCompanies(page)
      }
    },
    async getPromoteurs (page = 1) {
      let request = await this.$api.promoteurService.get({ page })
      if (page > 1) {
        let totalPromoteur = concat(request.data.items, this.promoteurs)
        this.$store.commit('setPromoteurs', { promoteurs: totalPromoteur })
      } else {
        this.$store.commit('setPromoteurs', { promoteurs: request.data.items })
      }
      if (request.data.page < request.data.pages) {
        page++
        this.getPromoteurs(page)
      }
    },
    async getPoles (page = 1) {
      let request = await this.$api.poleService.get({ page })
      if (page > 1) {
        let totalPoles = concat(request.data.items, this.poles)
        this.$store.commit('setPoles', { poles: totalPoles })
      } else {
        this.$store.commit('setPoles', { poles: request.data.items })
      }
      if (request.data.page < request.data.pages) {
        page++
        this.getPoles(page)
      }
    },
    getAllStates () {
      this.$api.stateService.get().then(r => {
        this.$store.commit('setAllStates', {
          states: r.data
        })
      })
    },
    loadProfils () {
      this.$api.profilService.get().then(r => {
        this.$store.commit('setProfilsData', {
          data: r.data
        })
      }).catch(() => {
        this.$notify({
          type: 'is-danger',
          text: 'Impossible de récupérer la liste des profils.'
        })
      })
    },
    loadRunningOptions () {
      let query = {
        per_page: 100,
        status: ['pending', 'waiting_list', 'approved']
      }
      this.$api.optionService.get(query).then(res => {
        this.$store.commit('setRunningOptions', {
          options: res.data.items
        })
      })
    },
    loadRunningDenunciations () {
      let query = {
        per_page: 100,
        status: ['pending']
      }
      this.$api.denunciationService.get(query).then(res => {
        console.log(res.data)
        this.$store.commit('setRunningDenunciations', {
          denunciations: res.data.items
        })
      })
    },
    loadCurrentUser () {
      this.$api.currentUserService.getOne().then(res => {
        this.$store.commit('setCurrentUser', { user: res.data })
        this.$fus.setToken(res.data.fus_token)
        if ('ably_token' in res.data) {
          this.initLiveApi(res.data.ably_token)
        }
      }).catch(() => {
        this.logout()
      })
    },
    loadUsersLength () {
      let query = {
        page: 1,
        per_page: 1,
        profil_type: ['seller', 'manager']
      }
      this.$api.userService.get(query).then(r => {
        this.$store.commit('setUsers', { users: r.data.total })
      })
    },
    loadEvents () {
      this.$api.eventService.get().then(r => {
        this.$store.commit('setEvents', { events: r.data })
      })
    },
    loadWishlist () {
      this.$api.lotService.getWishlist().then(r => {
        this.$store.commit('setWishlist', { wishlists: r.data })
      })
    },
    loadGoogleApiScripts () {
      let app = document.getElementById('app')
      let script = document.createElement('script')
      script.type = 'text/javascript'
      script.onload = () => {
        window.gapi.load('client:auth2', this.initGoogleClient)
      }
      script.async = true
      script.src = 'https://apis.google.com/js/api.js'
      app.appendChild(script)
    },
    logout () {
      bus.$emit(EVENTS.LOGOUT)
      this.$store.commit('logout')
      this.$router.push({ name: 'Login' })
      localStorage.removeItem('token')
    }
  },
  async created () {
    const APIROOT = this.$configs.APIROOT

    await axios.get(`${APIROOT}/config`).then(async configRequest => {
      let apiConfig = configRequest.data

      this.$store.commit('setApiReadyState', { isReady: true })

      const envConf = {
        ALGOLIA_APP_ID: apiConfig.ALGOLIA_APP_ID,
        ALGOLIA_SEARCH_KEY: apiConfig.ALGOLIA_SEARCH_KEY,
        ALGOLIA_PLACE_APP_ID: apiConfig.ALGOLIA_PLACE_APP_ID,
        ALGOLIA_PLACE_APP_KEY: apiConfig.ALGOLIA_PLACE_APP_KEY,
        APP_SENTRY_DSN: apiConfig.APP_SENTRY_DSN,
        CLOUDINARY_CLOUD_NAME: apiConfig.CLOUDINARY_CLOUD_NAME,
        CLOUDINARY_FOLDER: apiConfig.CLOUDINARY_FOLDER,
        COMPANY: apiConfig.COMPANY,
        COMPANY_COLOR: apiConfig.COMPANY_COLOR,
        CONTENTFUL_SPACE_ID: apiConfig.CONTENTFUL_SPACE_ID,
        CONTENTFUL_ACCESS_TOKEN: apiConfig.CONTENTFUL_ACCESS_TOKEN,
        ENABLE_GOOGLE_APIS: apiConfig.ENABLE_GOOGLE_APIS,
        FAQ_ENABLED: !!apiConfig.FAQ_ENABLED || false,
        GOOGLE_API_KEY: apiConfig.GOOGLE_API_KEY,
        GOOGLE_CLIENT_ID: apiConfig.GOOGLE_CLIENT_ID,
        GOOGLE_GA_ID: apiConfig.GOOGLE_GA_ID,
        GRAPHQL_API: apiConfig.GRAPHQL_API,
        LOGO_LOGIN: apiConfig.LOGO_LOGIN,
        LOGO_MAIN: apiConfig.LOGO_MAIN,
        LOGO_MAIN_LANDSCAPE: apiConfig.LOGO_MAIN_LANDSCAPE,
        ILLUSTRATION_LOGIN: apiConfig.ILLUSTRATION_LOGIN,
        MODE: apiConfig.MODE,
        MULTIDIFF_ENABLED: apiConfig.MULTIDIFF_ENABLED || false,
        MOBILE_HOST: apiConfig.MOBILE_HOST,
        LEAD_ENABLED: apiConfig.LEAD_ENABLED || false,
        SOCKET_URL: apiConfig.SOCKET_URL,
        SOCKET_ID: apiConfig.SOCKET_ID,
        STATS_ENABLED: apiConfig.STATS_ENABLED || false,
        GED_ENABLED: apiConfig.GED_ENABLED || 0,
        DIGITAL_SIGNATURE_ENABLED: apiConfig.DIGITAL_SIGNATURE_ENABLED || 0,
        LRE_ENABLED: apiConfig.LRE_ENABLED || 0,
        FUS_API: apiConfig.FUS_API,
        VADIMM_PUBLIC: apiConfig.VADIMM_PUBLIC,
        YOUSIGN_URL: apiConfig.YOUSIGN_URL,
        YOUSIGN_SIGNATURE_ID: apiConfig.YOUSIGN_SIGNATURE_ID || null,
        GED_CONTACTS: apiConfig.GED_CONTACTS || [],
        COUNTRY: apiConfig.COUNTRY || 'FRA',
        CURRENCY: apiConfig.CURRENCY || 'EUR'
      }
      // console.log('envConf > ', envConf)
      merge(_configs, envConf)
      merge(_configs, apiConfig)
      this.$store.commit('setConfigs', _configs)
      document.querySelector('title').text = _configs.COMPANY

      if (_configs.NODE_ENV === 'production' && _configs.APP_SENTRY_DSN) {
        Sentry.init({
          dsn: _configs.APP_SENTRY_DSN,
          integrations: [new Sentry.Integrations.Vue({ Vue })]
        })
      }
      Object.defineProperty(Vue.prototype, '$configs', { value: _configs })
      if (_configs.CURRENCY === 'MAD') {
        Object.defineProperty(Vue.prototype, '$currency', { value: 'MAD' })
        Object.defineProperty(Vue.prototype, '$faCurrency', { value: 'mad' })
      } else {
        Object.defineProperty(Vue.prototype, '$currency', { value: '€' })
        Object.defineProperty(Vue.prototype, '$faCurrency', { value: 'euro' })
      }
      const fusClient = new FileUploadService.Client({ host: _configs.FUS_API })
      Object.defineProperty(Vue.prototype, '$fus', { value: fusClient })

      const algoliaClient = algoliasearch(_configs.ALGOLIA_APP_ID, _configs.ALGOLIA_SEARCH_KEY)
      Object.defineProperty(Vue.prototype, '$algolia', { value: algoliaClient })

      if (_configs.NODE_ENV === 'production' && _configs.GOOGLE_GA_ID) {
        Vue.use(VueAnalytics, {
          id: _configs.GOOGLE_GA_ID,
          router
        })
      }
      Vue.use(VueGoogleMaps, {
        load: {
          key: _configs.GOOGLE_API_KEY,
          libraries: 'places'
        }
      })

      const token = localStorage.getItem('token')

      this.$api.setAppHost(this.$configs.APPHOST)
      if (token) {
        let user = jwtDecode(token)
        const userId = user.user_pk
        this.$store.commit('login', {
          userId,
          user
        })
        this.$api.setToken(token)
        this.initApp()
      }

      bus.$on(EVENTS.LOGIN, this.initApp)
      bus.$on(EVENTS.UPDATE_OPTION, this.loadRunningOptions)
      bus.$on(EVENTS.NEW_OPTION, this.loadRunningOptions)
      bus.$on(EVENTS.LOGOUT, () => {
        this.ablyClient.close()
      })
      bus.$on(EVENTS.INIT_GOOGLE_API, this.initGoogleClient)
      JSON.parse(_configs.ENABLE_GOOGLE_APIS)
      if (_configs.NODE_ENV === 'production' || JSON.parse(_configs.ENABLE_GOOGLE_APIS)) {
        this.loadGoogleApiScripts()
      }
      // import HelperDirective after store init
      await require('@/utils/HelperDirective')
    }).catch(e => {
      console.log(e)
      console.log('API NOT REACHABLE')
      this.$notify({
        type: 'is-danger',
        text: `Connexion impossible. Veuillez re-essayer ulterieurement.`
      })
      this.$router.replace({ name: 'Login' })
    })
  }
})



// WEBPACK FOOTER //
// ./src/main.js